import { clientConfig } from "../config";
import * as SessionStorageWrapper from "../utils/SessionStorageWrapper";

/**
 * Single entry point for Kameleoon calls (custom data, goals, events). Everything goes
 * through window.kameleoonQueue, so it is safe before and after engine.js loads.
 */

const PROD_SITE_CODE = "b04tle0b39";

// goal key -> [prod id, stage id]
export const KAMELEOON_GOALS: Record<string, [number, number]> = {
  cms_click_to_funnel: [434364, 434383],
  start_consultation_viewed: [434365, 434384],
  questionnaire_started: [434366, 434385],
  questionnaire_skipped: [434367, 434386],
  questionnaire_completed: [434368, 434387],
  sign_up: [434369, 434388],
  choose_treatment_viewed: [434370, 434389],
  product_added: [434371, 434390],
  shipping_viewed: [434372, 434391],
  view_payment_page: [434373, 428002],
  premium_upsell_accepted: [434374, 434392],
  otc_upsell_accepted: [434375, 434393],
  express_upsell_accepted: [434376, 434394],
};

const CATEGORY_BUCKETS: Record<string, string> = {
  medical_cannabis: "cannabis",
  erectile_dysfunction: "ed",
  weight_loss: "weight_loss",
};

const queue = () => {
  window.kameleoonQueue = window.kameleoonQueue || [];
  return window.kameleoonQueue;
};

/** Kameleoon custom-data value for a catalog slug (route mention / category param). */
export const categoryBucket = (slug: string) => CATEGORY_BUCKETS[slug] || "other";

/**
 * Page values Kameleoon reads itself (custom data with retrieval method "Custom JavaScript code",
 * `return window.kameleoonPageData && window.kameleoonPageData.<name>`). A pushed value can be lost
 * when the engine reloads on an SPA route change (it rebuilds the visit from storage ~100 ms later),
 * so the engine pulls these at evaluation time instead.
 */
export const setPageData = (name: string, value: string) => {
  window.kameleoonPageData = { ...(window.kameleoonPageData || {}), [name]: value };
};

export const setCustomData = (name: string, value: string | number | boolean) => {
  queue().push(["Data.setCustomData", name, value, true]);
};

/** Back to "No value": Kameleoon keeps a visit value until it is overwritten. */
export const resetCustomData = (name: string) => {
  queue().push(["Data.resetCustomData", name]);
};

export const trackGoal = (goalKey: string) => {
  const ids = KAMELEOON_GOALS[goalKey];
  if (!ids) {
    window.clientLogger?.warn("Kameleoon goal skipped: unknown goal key", {
      code: "kameleoon_unknown_goal",
      goalKey,
    });
    return;
  }
  queue().push(["Goals.processConversion", clientConfig.KAMELEOON_SITE_CODE === PROD_SITE_CODE ? ids[0] : ids[1]]);
};

export const triggerEvent = (name: string) => {
  queue().push(["Events.trigger", name]);
};

type FunnelEventRule = { goal?: string; event?: string; when?: (data: any) => boolean };

const OTC_ADD_CLICKS = ["add_to_cart", "search_add_to_cart"];

// Payment page URL is /payment/:country/:language/:order_hash_id; fire once per order hash,
// so a refused payment reloading the page does not count again (same trick as the thank-you page).
const isFirstPaymentPageView = () => {
  const orderHash = window.location.pathname.split("/")[4];
  if (!orderHash || SessionStorageWrapper.getItem("kameleoon_payment_order") === orderHash) return false;
  SessionStorageWrapper.setItem("kameleoon_payment_order", orderHash);
  return true;
};

// Mixpanel event name -> Kameleoon goal and/or event, fired from MainTracker.track.
export const FUNNEL_EVENT_RULES: Record<string, FunnelEventRule> = {
  "View start-consultation Page": { goal: "start_consultation_viewed" },
  "View Questionnaire Page": { goal: "questionnaire_started" }, // tracked once per load = first question
  "skipped q'r": { goal: "questionnaire_skipped" },
  questionnaire_completed: { goal: "questionnaire_completed" },
  "View Choose Treatment Page": { goal: "choose_treatment_viewed" },
  product_added: { goal: "product_added" },
  "View Page: shipping": { goal: "shipping_viewed" },
  "View Payment Page": { goal: "view_payment_page", when: isFirstPaymentPageView },
  Upsell_Popup_Shown: { event: "premium_upsell_shown" },
  Upsell_Popup_Converted: { goal: "premium_upsell_accepted" },
  "Funnel OTC Popup Display": { event: "otc_popup_shown" },
  "Funnel OTC Popup Click": { goal: "otc_upsell_accepted", when: (data) => OTC_ADD_CLICKS.includes(data?.click_type) },
  "Funnel Delivery Upsell Popup": { event: "express_upsell_shown" },
  "Funnel Delivery Upsell Popup Apply": { goal: "express_upsell_accepted" },
};

export const trackFunnelEvent = (eventName: string, data?: any) => {
  const rule = FUNNEL_EVENT_RULES[eventName];
  if (!rule || (rule.when && !rule.when(data))) return;
  if (rule.goal) trackGoal(rule.goal);
  if (rule.event) triggerEvent(rule.event);
};
